import React from 'react'
import { Link } from 'react-router-dom'

import { Alert, Heading, Stack } from 'component-library-next'

import { ReactComponent as GavelIcon } from '@app/assets/Small/Outline/Gavel.svg'
import { OpenAppointment, OpenAppointmentType } from '@app/graphql/types'
import { FormattedDate, useIntl } from '@app/intl/Intl'
import { ProductType } from '@app/lib/display/ProductType/ProductType'
import { Card } from '@app/lib/layout/Card/Card'
import { CardBlock } from '@app/lib/layout/Card/CardBlock'
import { CardHeader } from '@app/lib/layout/Card/CardHeader'
import { LineItem } from '@app/lib/layout/LineItem/LineItem'
import { Table, TableColumn } from '@app/lib/layout/Table/Table'
import { Text } from '@app/lib/typography/Text/Text'
import { useAppointmentsTable } from '@app/modules/Appointments/hooks/useAppointmentsTable'
import { getCourtCasePath } from '@app/modules/CourtCases'
import { getMessagesPath } from '@app/modules/Messages'

const getRedirectPath = (openAppointmentItem: OpenAppointment): string => {
  if (openAppointmentItem.type === OpenAppointmentType.Alc) {
    return getMessagesPath(openAppointmentItem.customer.id)
  }

  return getCourtCasePath(openAppointmentItem.customer.id, openAppointmentItem.courtCaseUuid!)
}

export const AppointmentsWidget = () => {
  const { formatMessage } = useIntl()

  const { data, loading, error } = useAppointmentsTable()

  const columns: Array<TableColumn<OpenAppointment>> = [
    {
      key: 'customerFullName',
      label: formatMessage({ id: 'Lawyer.Appointments.Table.Column.Name' }),
      value: (openAppointmentItem) => (
        <Link to={getRedirectPath(openAppointmentItem)}>
          {[openAppointmentItem.customer.firstName, openAppointmentItem.customer.lastName]
            .filter(Boolean)
            .join(' ')}
        </Link>
      )
    },
    {
      key: 'topic',
      label: formatMessage({ id: 'Lawyer.Appointments.Table.Column.Topic' }),
      value: (openAppointmentItem) => (
        <>
          <Link to={getRedirectPath(openAppointmentItem)}>
            {openAppointmentItem.customer.latestMessage?.topic.name}
          </Link>
          {openAppointmentItem.type === OpenAppointmentType.CourtCase && (
            <GavelIcon style={{ height: '1rem', width: '1rem', marginLeft: '0.5rem' }} />
          )}
        </>
      )
    },
    {
      key: 'productName',
      label: formatMessage({ id: 'Lawyer.Appointments.Table.Column.ProductName' }),
      value: (openAppointmentItem) => (
        <ProductType {...openAppointmentItem.customer.membership?.membershipType} />
      )
    },
    {
      key: 'appointmentAt',
      label: formatMessage({ id: 'Lawyer.Appointments.Table.Column.AppointmentAt' }),
      value: (openAppointmentItem) => {
        return (
          <FormattedDate
            day='2-digit'
            month='2-digit'
            year='numeric'
            hour='2-digit'
            minute='2-digit'
            value={openAppointmentItem.date}
          />
        )
      }
    }
  ]

  return (
    <Card>
      <CardHeader>
        <LineItem
          label={
            <Heading size={Heading.sizes.small}>
              {formatMessage({ id: 'Lawyer.Appointments.Heading' })}
            </Heading>
          }
        />
      </CardHeader>
      {error && (
        <CardBlock>
          <Alert color={Alert.colors.warning}>
            {formatMessage({ id: 'Lawyer.Appointments.Table.Error' })}
          </Alert>
        </CardBlock>
      )}
      <Stack>
        <Table
          columns={columns}
          items={data?.openAppointments.items || []}
          loading={loading}
          noResults={formatMessage({ id: 'Lawyer.Appointments.Table.NoResults' })}
        />
        <Text color='primary' weight='bold'>
          {formatMessage(
            { id: 'Lawyer.Appointments.Pagination.TotalEntries' },
            {
              totalEntries: data?.openAppointments.items.length
            }
          )}
        </Text>
      </Stack>
    </Card>
  )
}
